import { login } from '@findigs/api/index.server';
import { loginSchema } from '@findigs/api/src/schemas/account/login';
import { enforceLocalRoute } from '@findigs/helpers/src/helpers/enforce-local-route';
import {
  FIRST_FIELD,
  LAST_FIELD,
  makeFormSchema,
} from '@findigs/helpers/src/helpers/make-form';
import { useRouteNavigationState } from '@findigs/helpers/src/hooks/use-route-navigation-state';
import { resetPosthogIfIdentifiedOnInit } from '@findigs/helpers/src/integrations/posthog/posthog.client';
import {
  getHeadersToSetSSOSession,
  getSSOSession,
  schemaSSO,
} from '@findigs/helpers/src/sessions/session.sso.server';
import { Button } from '@findigs/ui-kit/src/components/button';
import { FormAlert } from '@findigs/ui-kit/src/components/form-alert';
import { FormInput } from '@findigs/ui-kit/src/components/form-input';
import { FormInputPassword } from '@findigs/ui-kit/src/components/form-input-password';
import { Link } from '@findigs/ui-kit/src/components/link';
import { ProgressView } from '@findigs/ui-kit/src/components/progress-view';
import { tintsNeutral90 } from '@findigs/ui-kit/src/css/colors/_colors.module.scss';
import { json } from '@remix-run/node';
import type { MetaFunction } from '@remix-run/node';
import {
  useActionData,
  useLoaderData,
  useSearchParams,
} from '@remix-run/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { z } from 'zod';
import { ErrorBoundaryWithMain } from '~/error-boundary.base';
import { isLocalUrl } from '~/helpers/is-local-url';
import { useSetJWTLocalStorage } from '~/hooks/use-set-jwt-local-storage';
import {
  getHeadersToSetJWTSession,
  getJWTSession,
  setJWTSession,
} from '~/sessions/session.token.server';
import type { LocalRoute, RemixAction, RemixLoader } from '~/types';

export const meta: MetaFunction = () => {
  return [
    { title: 'Login | Findigs' },
    {
      property: 'og:title',
      content: 'Welcome to Findigs, please login.',
    },
    { name: 'description', content: 'Welcome to Findigs, please login.' },
  ];
};

export const handle: RouteHandle = {
  navbar: 'Simple',
  footer: 'Simple',
  navbarLogoHref: false,
};

type SSOSessionType = z.infer<typeof schemaSSO>['type'];

const isManagerEquivalent = (type: SSOSessionType): boolean =>
  type === 'manager' || type === 'underwriter';

const { Form, FormSlot, validator, makeFormError } = makeFormSchema({
  form: {},
  schema: [loginSchema, loginSchema.shape],
  fields: [
    {
      name: 'email',
      component: FormInput,
      props: { label: 'Email', required: true, className: 'pb-4' },
    },
    {
      name: 'password',
      component: FormInputPassword,
      props: {
        label: 'Password',
        required: true,
        className: 'pb-6',
        disableReveal: false,
      },
    },
  ],
} as const);

export const loader = async function ({ request }) {
  const url = new URL(request.url);
  const redirectTo = enforceLocalRoute(
    url.searchParams.get('redirectTo') ?? '/'
  );

  // checks to see if creds are present in the SSO session, and if so,
  // persists them into the JWT session
  const sso = schemaSSO.safeParse(await getSSOSession(request));
  if (sso.success && isManagerEquivalent(sso.data.type)) {
    await setJWTSession(
      request,
      sso.data,
      url.toString().replace(url.origin, '') as LocalRoute
    );
  }

  const { jwt } = await getJWTSession(request);

  if (jwt) {
    return json({ jwt, redirectTo, redirectExternal: redirectTo });
  }

  const email = url.searchParams.get('email') ?? undefined;

  return json({
    redirectTo,
    email,
    isAuth0RedirectDisabledByEnv: process.env.DISABLE_AUTH0_REDIRECT === 'true',
  });
} satisfies RemixLoader;

export const action = async function ({ request }) {
  const form = await validator.validate(await request.formData());
  if (form.error) {
    return makeFormError(form);
  }

  const { result, error } = await login(form.data);
  if (!result) {
    return makeFormError(form, error);
  }

  if (!isManagerEquivalent(result.user.type)) {
    const headers = await getHeadersToSetSSOSession(request, {
      jwt: result.token,
      userId: result.user.pk,
      type: result.user.type,
    });

    const { host, port, protocol } = new URL(process.env.RENT_DOMAIN as string);
    const url = new URL(request.url);
    Object.assign(url, { protocol, host, port });
    url.searchParams.append('fromPMLogin', 'true');

    //TODO: DOMO-1781: use redirectDocument once Remix is updated to v2
    return json({ redirectExternal: url.toString() }, { headers });
  }

  const url = new URL(request.url);
  const redirectTo = enforceLocalRoute(
    url.searchParams.get('redirectTo') ?? '/'
  );

  if (isLocalUrl(redirectTo)) {
    await setJWTSession(
      request,
      { jwt: result.token, userId: result.user.pk },
      redirectTo
    );
  } else {
    //TODO: DOMO-1781: use redirectDocument once Remix is updated to v2
    return json(
      { jwt: result.token, redirectExternal: redirectTo },
      {
        headers: await getHeadersToSetJWTSession(request, {
          jwt: result.token,
          userId: result.user.pk,
        }),
      }
    );
  }

  return null;
} satisfies RemixAction;

const restorePasswordPath: string = '/forgot';

export default function Index() {
  const loaderData = useLoaderData<typeof loader>() ?? {};
  const actionData = useActionData<typeof action>() ?? {};

  const ssoFormRef = useRef<HTMLFormElement>(null);

  const handleSingleSignOn = useCallback(() => {
    ssoFormRef.current?.submit();
  }, []);

  const { redirectTo, email, isAuth0RedirectDisabledByEnv } = loaderData;
  const { non_field_errors } = actionData;
  const { jwt, redirectExternal } = { ...loaderData, ...actionData };

  const [searchParams] = useSearchParams();

  const isAuth0RedirectEnabled = useMemo(() => {
    return (
      !searchParams.has('disableRedirect') && !isAuth0RedirectDisabledByEnv
    );
  }, [searchParams, isAuth0RedirectDisabledByEnv]);

  useEffect(() => {
    // Authenticated users redirect in-app below; auto-submitting SSO would 404.
    if (isAuth0RedirectEnabled && !jwt) {
      handleSingleSignOn();
    }
  }, [isAuth0RedirectEnabled, handleSingleSignOn, jwt]);

  const isVerifyingEmail = searchParams
    .get('redirectTo')
    ?.includes('verify_email');

  useSetJWTLocalStorage(jwt);

  const loading =
    useRouteNavigationState() !== 'idle' || Boolean(redirectExternal);

  //TODO: DOMO-1781: use redirectDocument once Remix is updated to v2
  useEffect(() => {
    if (
      (redirectExternal && jwt) ||
      redirectExternal?.includes('fromPMLogin=')
    ) {
      // Give some time for jwt to set in local storage
      const timer = setTimeout(() => {
        window.location.replace(redirectExternal);
      }, 10);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [redirectExternal, jwt]);

  useEffect(() => {
    resetPosthogIfIdentifiedOnInit();
  }, []);

  return (
    <main className="grid pb-20">
      <div className="start-1 col-4 sm:start-2 sm:col-4 md:start-3 md:col-4 lg:start-4 lg:col-5 pb-12">
        <h1 className="text-t-sm sm:text-d-xs md:text-d-xs lg:text-d-xs pt-12 pb-6">
          {isVerifyingEmail ? `Welcome Back` : `Log in to Findigs`}
        </h1>
        <div className="text-b-md-regular lg:text-b-lg-regular text-muted">
          {isVerifyingEmail
            ? `Thanks for verifying your email address. Log in with the same credentials to continue.`
            : `Let’s simplify the way home.`}
        </div>
      </div>

      <div className="d-flex flex-column col-4 sm:start-7 sm:col-5 md:start-10 md:col-5 lg:start-15 lg:col-7">
        {!isAuth0RedirectEnabled && (
          <Form
            method="post"
            action={`/login?index&redirectTo=${encodeURIComponent(redirectTo)}`}
            style={{ position: 'relative' }}
            defaultValues={{ email }}
          >
            <FormSlot before={FIRST_FIELD}>
              <ProgressView className="loading" loading={loading} />

              {non_field_errors?.map((msg) => (
                <FormAlert className="mb-4" key={msg} variant="Error">
                  {msg}
                </FormAlert>
              ))}
            </FormSlot>

            <FormSlot after={LAST_FIELD}>
              <div className="pb-6">
                <div className="pb-6">
                  <Link
                    style={{ color: tintsNeutral90 }}
                    className="text-b-md-medium"
                    as="a"
                    href={restorePasswordPath}
                    inverse
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="pb-6">
                  <Link
                    style={{ color: tintsNeutral90 }}
                    className="text-b-md-medium"
                    as="button"
                    type="button"
                    onClick={handleSingleSignOn}
                    inverse
                  >
                    Sign in with SSO
                  </Link>
                </div>
              </div>
              <div className="pb-6 flex-column sm:d-unset md:d-unset lg:d-unset">
                <Button variant="FilledStandard" type="submit">
                  Login
                </Button>
              </div>
            </FormSlot>
          </Form>
        )}
      </div>

      <form
        ref={ssoFormRef}
        action={`/auth/auth0?redirectTo=${encodeURIComponent(redirectTo)}`}
        method="POST"
      />
    </main>
  );
}
export { ErrorBoundaryWithMain as ErrorBoundary };
